import React from 'react'

import { Value } from 'components/base/Form/Input/types'

import { Props } from './types'

import RadioOption from './Option'

import Radio from './index.presentational'

const RadioContainer: React.FC<Props> = ({
  children,
  createdByCorrectParent = false,
  defaultValue,
  loaded,
  loading,
  options,
  renderOptions,
  value: valueProp,
  ...htmlProps
}): React.ReactElement => {
  if (!createdByCorrectParent) {
    throw new Error(
      'Do not directly use the Form/Input/Radio component. Instead, use Form/Input and pass a `type` prop of `radio`.',
    )
  }

  const value = (valueProp === false) ? false : (valueProp || defaultValue)

  const processChild = (child: React.ReactNode): React.ReactNode => {
    if (!React.isValidElement(child)) {
      return child
    }

    if (child && child.type === RadioOption) {
      return React.cloneElement(child, {
        ...child.props,
        isChecked: child.props.value === value,
        name: htmlProps.name,
        onChange: (value: Value): void => {
          if (value === 'true' || value === 'false') {
            value = JSON.parse(value)
          }
          const event = { target: { value } }

          return (htmlProps.onChange && htmlProps.onChange(event as unknown as React.ChangeEvent<HTMLInputElement>)) || undefined
        },
      })
    }

    return child
  }

  const newChildren = (renderOptions && options && renderOptions(options).map(child => processChild(child))) ||
    (options && options.map(option => (
      React.createElement(
        RadioOption,
        {
          key: option,
          value: option,
        },
        option,
      )
    )).map(child => processChild(child))) ||
    (children && React.Children.map(children, child => processChild(child))) ||
    []

  return React.createElement(Radio, {
    ...htmlProps,
    onChange: (event, ...options) => {
      let { value } = event.target
      if (value === 'true' || value === 'false') {
        value = JSON.parse(value)
      }
      htmlProps.onChange && htmlProps.onChange({
        ...event,
        target: {
          ...event.target,
          value,
        },
      }, ...options)
    },
    showLoading: loading,
    showLoadingError: !loaded && !options && !React.Children.toArray(children).length,
    value,
  }, newChildren)
}

export * from './types'

export default RadioContainer
